import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RxHamburgerMenu, RxCross1 } from "react-icons/rx";
import { LuShoppingBasket } from "react-icons/lu";
import { FaUserCircle, FaSignOutAlt } from "react-icons/fa";
import OtpModal from "./otpModal";
import ProfileModal from "./profileModal";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = "https://supizza-backend.onrender.com";

export default function Navbar() {
  // ---------------- STATES ----------------
  const [mobile, setMobile] = useState(false);
  const [shadow, setShadow] = useState(false);
  const [cartChecker, setCartChecker] = useState(0);
  const [user, setUser] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);

  // OTP & Profile
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    address: "",
  });
  const [profileBackup, setProfileBackup] = useState(formData);

  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState("");

  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const [phoneError, setPhoneError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Orders
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");

  // ---------------- FETCH USER ----------------
  const fetchUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
        window.dispatchEvent(new Event("userLoggedOut"));
        return;
      }

      if (!res.ok) return;

      const data = await res.json();

      setUser(data.user || null);
      setFormData({
        name: data.user?.name || "",
        email: data.user?.email || "",
        phone: data.user?.phone || "",
        city: data.user?.city || "",
        address: data.user?.address || "",
      });

      // Sync with other components
      window.dispatchEvent(
        new CustomEvent("userUpdated", { detail: data.user }),
      );
    } catch (err) {
      console.error("Network error, keeping user logged in", err);
    }
  };

  useEffect(() => {
    fetchUser();

    // Listen to user updates from Checkout or elsewhere
    const handleUserUpdated = (e) => {
      if (e.detail) {
        setUser(e.detail);
        setFormData({
          name: e.detail.name,
          email: e.detail.email,
          phone: e.detail.phone,
          city: e.detail.city,
          address: e.detail.address,
        });
      } else {
        fetchUser(); // fallback
      }
    };
    const handleUserLoggedOut = () => {
      setUser(null);
      setFormData({ name: "", email: "", phone: "", city: "", address: "" });
      setOtpSent(false);
      setOtpInput("");
      setOpenDropdown(false);
    };

    window.addEventListener("userUpdated", handleUserUpdated);
    window.addEventListener("userLoggedOut", handleUserLoggedOut);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdated);
      window.removeEventListener("userLoggedOut", handleUserLoggedOut);
    };
  }, []);

  // ---------------- CART ----------------
  useEffect(() => {
    const updateCart = () => {
      const data = JSON.parse(localStorage.getItem("cart")) || [];
      setCartChecker(data.length);
    };
    updateCart();
    window.addEventListener("cartUpdated", updateCart);
    return () => window.removeEventListener("cartUpdated", updateCart);
  }, []);

  // ---------------- SCROLL SHADOW ----------------
  useEffect(() => {
    const handleScroll = () => setShadow(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ---------------- LOGOUT ----------------
  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setOtpSent(false);
    setOtpInput("");
    setFormData({ name: "", email: "", phone: "", city: "", address: "" });
    setOpenDropdown(false);
    showSuccess("Logged out!");
    window.dispatchEvent(new Event("userLoggedOut")); // triggers Checkout update
  };

  // ---------------- FETCH ORDERS ----------------
  const fetchOrders = async () => {
    if (!user) return setOrders([]);
    setLoadingOrders(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/orders/user/${user.email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      const data = await res.json();
      if (Array.isArray(data)) setOrders(data);
      else if (data.order) setOrders([data.order]);
      else setOrders([]);
    } catch (err) {
      console.error(err);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // ---------------- OTP HANDLERS ----------------
  const handleSendOtp = async () => {
    if (!formData.email) return;

    setSendingOtp(true); // disable button

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await res.json();

      if (res.ok) {
        setOtpSent(true);
        setResendTimer(30); // start countdown
        showSuccess("OTP sent! Check your email.");
      } else {
        showSuccess(data.message || "Error sending OTP.");
      }
    } catch (err) {
      console.error(err);
      showSuccess("Error sending OTP.");
    } finally {
      setSendingOtp(false); // re-enable button if needed
    }
  };
  useEffect(() => {
    if (!otpSent) return;
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [otpSent]);

  const handleVerifyOtp = async () => {
    if (!otpInput) return;
    setVerifyingOtp(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, otp: otpInput }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("token", data.token);
        setUser(data.user);
        setFormData({
          name: data.user.name || "",
          email: data.user.email || "",
          phone: data.user.phone || "",
          city: data.user.city || "",
          address: data.user.address || "",
        });
        setOtpInput("");
        setOtpModalOpen(false);
        // ✅ Show logged in toast
        toast.success("Logged in.", { duration: 2000 });

        // notify other components
        window.dispatchEvent(
          new CustomEvent("userUpdated", { detail: data.user }),
        );
      } else {
        setOtpError(data.message || "Invalid OTP");
      }
    } catch (err) {
      console.error(err);
      setOtpError("Error verifying OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  // ---------------- PROFILE ----------------
  const handleSaveInfo = async (e) => {
    e.preventDefault();
    if (!user || phoneError) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/auth/update-profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        setUser(data.user);
        setProfileModalOpen(false);
        showSuccess("Saved!");

        // dispatch event to notify user updated
        window.dispatchEvent(
          new CustomEvent("userUpdated", { detail: data.user }),
        );
      } else {
        console.error(data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------- INPUT CHANGE ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "phone") {
      const phoneRegex = /^03\d{9}$/;
      setPhoneError(
        phoneRegex.test(value)
          ? ""
          : "Mobile must start with 03 and be 11 digits long",
      );
    }
  };
  // ------------ form changed?---------------------
  const isUnchanged = () => {
    return (
      formData.name === profileBackup.name &&
      formData.phone === profileBackup.phone &&
      formData.city === profileBackup.city &&
      formData.address === profileBackup.address
    );
  };

  // ---------------- SUCCESS TOAST ----------------
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      setSuccessMessage("");
    }, 2000);
  };

  const filteredOrders = orders.filter((o) =>
    o.orderId?.toLowerCase().includes(orderSearch.toLowerCase()),
  );

  // ===================== JSX =====================
  return (
    <>
      <Toaster position="bottom-right" className="" />
      {/* ===================== NAVBAR ===================== */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          shadow
            ? "bg-white/80 backdrop-blur-xl shadow-lg"
            : "bg-red-700 backdrop-blur-md"
        }`}
      >
        {/* --- Desktop Navbar --- */}
        <div className="hidden lg:flex items-center justify-between h-[70px] max-w-7xl mx-auto px-6  ">
          <h1
            className={`text-3xl font-bold tracking-wide ${shadow ? "text-red-700" : "text-white"}`}
          >
            Supizza
          </h1>

          <div
            className={`flex items-center gap-10 font-thin tracking-wide  ${shadow ? "text-gray-700" : "text-white"}`}
          >
            <Link to="/">Home</Link>
            <Link to="/menu">Menu</Link>
            <Link to="/contact" state={{ user }}>
              Contact
            </Link>
          </div>

          <div
            className={`flex items-center gap-4 relative ${shadow ? "bg-red-600 px-3 py-2 rounded-full" : "bg-white px-3 py-2 rounded-full"}`}
          >
            {user && (
              <button
                onClick={() => {
                  fetchOrders();
                  setOrderDrawerOpen(true);
                }}
                className={`font-base ${shadow ? "bg-white text-gray-700" : "bg-red-600 text-white"} px-4 py-2 rounded-full`}
              >
                My Orders
              </button>
            )}

            <Link to="/cart" className="relative">
              <LuShoppingBasket
                className={`text-2xl ${shadow ? "text-white" : "text-gray-700"}`}
              />
              {cartChecker > 0 && (
                <span
                  className={`absolute -top-2 -right-3 ${shadow ? "bg-white text-gray-700" : "bg-red-600 text-white"} text-xs font-semibold px-2 py-0.5 rounded-full shadow-md`}
                >
                  {cartChecker}
                </span>
              )}
            </Link>

            <div
              className="relative cursor-pointer"
              onClick={() =>
                user ? setOpenDropdown(!openDropdown) : setOtpModalOpen(true)
              }
            >
              <FaUserCircle
                className={`text-2xl ${shadow ? "text-white" : "text-gray-700"}`}
              />

              {openDropdown && user && (
                <div className="absolute right-0 mt-4 w-64 bg-white rounded-xl shadow-xl border p-4 space-y-4 z-50 animate-fadeOut">
                  <div className="space-y-1">
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <div className="border-t pt-3 flex justify-between items-center">
                    <button
                      onClick={() => {
                        setProfileModalOpen(true);
                        setOpenDropdown(false);
                        setProfileBackup(formData);
                      }}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Edit Profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="text-gray-500 hover:text-red-600 transition"
                    >
                      <FaSignOutAlt className="text-lg" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* --- Mobile Navbar --- */}
        <div className="lg:hidden relative flex items-center h-[70px] px-4">
          <button
            onClick={() => {
              setMobile(!mobile);
              setOpenDropdown(false);
            }}
            className="z-10"
          >
            {mobile ? (
              <RxCross1
                className={`text-2xl ${shadow ? "text-red-700" : "text-white"}`}
              />
            ) : (
              <RxHamburgerMenu
                className={`text-2xl ${shadow ? "text-red-700" : "text-white"}`}
              />
            )}
          </button>

          <h1
            className={`absolute ${user ? "left-1/4" : "left-1/2"} -translate-x-1/2 text-2xl font-bold duration-300 ease-in-out ${shadow ? "text-red-700" : "text-white"}`}
          >
            Supizza
          </h1>

          <div
            className={`ml-auto flex items-center gap-3 ${shadow ? "bg-red-600 px-3 py-2 rounded-full" : "bg-white px-3 py-2 rounded-full"}`}
          >
            {user && (
              <button
                onClick={() => {
                  fetchOrders();
                  setOrderDrawerOpen(true);
                }}
                className={`text-xs font-semibold px-3 py-1  duration-300 ease-in-out rounded-full ${shadow ? "bg-white text-gray-700" : "bg-red-600 text-white"}`}
              >
                Orders
              </button>
            )}

            <Link to="/cart" className="relative">
              <LuShoppingBasket
                className={`text-xl ${shadow ? "text-white" : "text-gray-700"}`}
              />
              {cartChecker > 0 && (
                <span
                  className={`absolute -top-2 -right-3 ${shadow ? "bg-white text-gray-700" : "bg-red-600 text-white"} text-xs font-semibold px-[5px] rounded-full shadow-md`}
                >
                  {cartChecker}
                </span>
              )}
            </Link>

            <div
              className="relative cursor-pointer"
              onClick={() => {
                setMobile(false);
                user ? setOpenDropdown(!openDropdown) : setOtpModalOpen(true);
              }}
            >
              <FaUserCircle
                className={`text-xl ${shadow ? "text-white" : "text-gray-700"}`}
              />

              {openDropdown && user && (
                <div className="absolute right-0 bg-white rounded-xl shadow-xl border p-4 space-y-4 z-50 w-48 mt-2 animate-fadeOut">
                  <div className="space-y-1">
                    <p className="font-medium text-sm text-gray-900 truncate">
                      {user.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user.email}
                    </p>
                  </div>
                  <div className="border-t pt-3 flex justify-between items-center">
                    <button
                      onClick={() => {
                        setProfileModalOpen(true);
                        setOpenDropdown(false);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Edit Profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="text-gray-500 hover:text-red-600 transition"
                    >
                      <FaSignOutAlt className="text-base" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* --- Mobile Dropdown Links --- */}
        <AnimatePresence>
          {mobile && (
            <>
              {/* Blur Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 z-[9998]"
                onClick={() => setMobile(false)}
              />

              {/* Drawer */}
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", stiffness: 180, damping: 25 }}
                className="fixed top-0 left-0 h-screen w-[60%] max-w-[340px] bg-white z-[9999] shadow-md flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b">
                  <h2 className="text-xl font-bold tracking-wide text-red-600">
                    Supizza
                  </h2>

                  <button
                    onClick={() => setMobile(false)}
                    className="text-2xl text-gray-500 hover:text-red-600 transition"
                  >
                    ✕
                  </button>
                </div>

                {/* Links */}
                <nav className="flex flex-col px-6 py-8">
                  <Link
                    to="/"
                    onClick={() => setMobile(false)}
                    className="py-3 text-base font-medium text-gray-800 hover:text-red-600 border-b border-gray-100 transition"
                  >
                    Home
                  </Link>

                  <Link
                    to="/menu"
                    onClick={() => setMobile(false)}
                    className="py-3 text-base font-medium text-gray-800 hover:text-red-600 border-b border-gray-100 transition"
                  >
                    Menu
                  </Link>

                  <Link
                    to="/contact"
                    state={{ user }}
                    onClick={() => setMobile(false)}
                    className="py-3 text-base font-medium text-gray-800 hover:text-red-600 border-b border-gray-100 transition"
                  >
                    Contact
                  </Link>
                </nav>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
      {/* ORDER DRAWER */}
      <AnimatePresence>
        {orderDrawerOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setOrderDrawerOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />

            {/* Drawer */}
            <motion.div
              className="relative ml-auto w-full max-w-md h-full bg-white shadow-xl p-6 overflow-y-auto"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 25,
              }}
            >
              <button
                className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-2xl"
                onClick={() => setOrderDrawerOpen(false)}
              >
                <RxCross1 />
              </button>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Your Orders
              </h2>

              {/* Search */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Search by Order ID..."
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  className="w-full border rounded-lg p-3 pr-10 focus:ring-2 focus:ring-red-500 outline-none"
                />

                {orderSearch && (
                  <button
                    onClick={() => setOrderSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600"
                  >
                    <RxCross1 />
                  </button>
                )}
              </div>

              {loadingOrders ? (
                <p className="text-gray-700">Loading orders...</p>
              ) : orders.length === 0 ? (
                <div className="text-center space-y-4">
                  <p className="text-gray-700">
                    You haven’t placed any orders yet.
                  </p>

                  <Link
                    to="/menu"
                    className="inline-block bg-red-600 text-white px-4 py-2 rounded-xl hover:bg-red-700 transition"
                    onClick={() => setOrderDrawerOpen(false)}
                  >
                    Order Now
                  </Link>
                </div>
              ) : filteredOrders.length === 0 ? (
                <p className="text-gray-700">No orders match your search.</p>
              ) : (
                filteredOrders.map((order, index) => {
                  const totalItems = order.cartItems.reduce(
                    (acc, item) => acc + item.quantity,
                    0,
                  );

                  const formatPKR = (num) =>
                    Number(num).toLocaleString("en-PK");

                  return (
                    <motion.div
                      key={order._id}
                      className="bg-gray-50 p-4 rounded-xl shadow-sm mb-4 border"
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.08 }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold text-gray-800">
                          Order #{order.orderId || order._id}
                        </h3>

                        <span className="text-sm text-gray-500">
                          {new Date(order.createdAt).toLocaleString("en-PK")}
                        </span>
                      </div>

                      <div className="text-gray-600 text-sm mb-4">
                        {order.cartItems.map((item, i) => (
                          <div key={i} className="flex justify-between mb-2">
                            <div className="flex items-start gap-1 max-w-[65%]">
                              <span className="truncate">
                                {item.quantity} × {item.name}
                              </span>

                              {item.size && (
                                <span className="text-gray-400 text-xs uppercase whitespace-nowrap">
                                  ({item.size})
                                </span>
                              )}
                            </div>

                            <span className="font-medium text-gray-800">
                              Rs {formatPKR(item.price * item.quantity)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-2 text-gray-700 text-sm space-y-1">
                        <div className="flex justify-between">
                          <span>Total Items</span>
                          <span>{totalItems}</span>
                        </div>

                        <div className="flex justify-between">
                          <span>Subtotal</span>
                          <span>Rs {formatPKR(order.subtotal)}</span>
                        </div>

                        <div className="flex justify-between">
                          <span>Tax</span>
                          <span>Rs {formatPKR(order.tax)}</span>
                        </div>

                        <div className="flex justify-between">
                          <span>Delivery</span>
                          <span>Rs {formatPKR(order.delivery)}</span>
                        </div>

                        <div className="flex justify-between text-base sm:text-lg font-semibold text-gray-900 pt-2 border-t">
                          <span>Grand Total</span>
                          <span>Rs {formatPKR(order.totalAmount)}</span>
                        </div>

                        <div className="flex justify-between pt-1">
                          <span>Payment Method</span>
                          <span>{order.paymentMethod}</span>
                        </div>

                        <div
                          className={`w-full block py-2 text-center rounded-xl px-2 text-xs mt-3 ${
                            order.status === "pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : order.status === "out_for_delivery"
                                ? "bg-blue-100 text-blue-700"
                                : order.status === "delivered"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                          }`}
                        >
                          {order.status === "pending"
                            ? "Pending"
                            : order.status === "out_for_delivery"
                              ? "Out for Delivery"
                              : order.status === "delivered"
                                ? "Delivered"
                                : "Cancelled"}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* OTP Modal */}
      <OtpModal
        otpModalOpen={otpModalOpen}
        setOtpModalOpen={setOtpModalOpen}
        user={user}
        otpSent={otpSent}
        formData={formData}
        setFormData={setFormData}
        handleSendOtp={handleSendOtp}
        otpInput={otpInput}
        setOtpInput={setOtpInput}
        otpError={otpError}
        setOtpError={setOtpError}
        verifyingOtp={verifyingOtp}
        handleVerifyOtp={handleVerifyOtp}
        resendTimer={resendTimer}
        sendingOtp={sendingOtp}
        setSendingOtp={setSendingOtp}
      />

      {/* Profile Modal */}
      <ProfileModal
        profileModalOpen={profileModalOpen}
        setProfileModalOpen={() => setProfileModalOpen(false)}
        user={user}
        formData={formData}
        handleChange={handleChange}
        handleSaveInfo={handleSaveInfo}
        isUnchanged={isUnchanged()}
        phoneError={phoneError}
        profileBackup={profileBackup}
      />

      {/* SUCCESS TOAST */}
      {success && (
        <div className="fixed bottom-5 right-5 bg-white/90 backdrop-blur-md border border-gray-300 shadow-lg px-6 py-3 rounded-xl animate-fadeIn scaleIn z-50">
          {successMessage}
        </div>
      )}
    </>
  );
}
